import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getMobileRouteChrome } from './mobileRouteChrome';

const skeletonClass =
  'animate-pulse rounded-[var(--rely-radius-control)] bg-[var(--rely-surface-elevated)]';

export default function RouteLoadingFallback({ layoutType = 'empresa' }) {
  const { t } = useTranslation();
  const location = useLocation();
  const mobileChrome = getMobileRouteChrome(location.pathname, { layoutType });

  return (
    <section
      role="status"
      aria-busy="true"
      data-testid="route-loading-fallback"
      data-mobile-safe-area={
        mobileChrome.reserveDockClearance ? 'dock' : undefined
      }
      className="min-h-[calc(100dvh-4rem)] bg-[var(--rely-surface-page)] text-[var(--rely-text-strong)]"
    >
      <span className="sr-only">{t('routeLoading.page', 'Loading page')}</span>

      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-3">
            <div className={`${skeletonClass} h-4 w-24`} />
            <div className={`${skeletonClass} h-8 w-full max-w-[18rem]`} />
            <div className={`${skeletonClass} h-4 w-full max-w-[24rem]`} />
          </div>
          <div
            className={`${skeletonClass} hidden h-10 w-28 shrink-0 sm:block`}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3 sm:gap-4">
          {[0, 1, 2].map((item) => (
            <div
              key={item}
              className="rounded-[var(--rely-radius-surface)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] p-4 shadow-[var(--rely-shadow-panel)]"
            >
              <div className={`${skeletonClass} mb-4 h-8 w-8`} />
              <div className={`${skeletonClass} mb-2 h-4 w-24`} />
              <div className={`${skeletonClass} h-3 w-full`} />
            </div>
          ))}
        </div>

        <div className="rounded-[var(--rely-radius-panel)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] p-4 shadow-[var(--rely-shadow-panel)] sm:p-5">
          <div className={`${skeletonClass} mb-5 h-5 w-36`} />
          <div className="space-y-3">
            {[0, 1, 2, 3].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 border-t border-[var(--rely-border-subtle)] pt-3 first:border-t-0 first:pt-0"
              >
                <div className={`${skeletonClass} h-9 w-9 shrink-0`} />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className={`${skeletonClass} h-3 w-3/4`} />
                  <div className={`${skeletonClass} h-3 w-1/2`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
